/*
 * Copyright (c) 2017-2026 Brighter AI Technologies GmbH. All rights reserved.
 * Unauthorized use, copying, reproduction, or distribution of this file, via any medium,
 * is strictly prohibited.
 */

import React from "react";
import {
  SaveButton,
  useNotify,
  useDataProvider,
  useRedirect,
} from "react-admin";
import {
  CardCvcElement,
  CardNumberElement,
  CardExpiryElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import ArrowLeft from "@material-ui/icons/ArrowLeft";
import {
  Card,
  Button,
  CardContent,
  CardHeader,
  CircularProgress,
  TextField,
  Grid,
} from "@material-ui/core";
import { loadStripe } from "@stripe/stripe-js";
import StripeInput from "../../commons/StripeInput";

const CardEditContent = () => {
  const [loading, setLoading] = React.useState(false);
  const [card, setCard] = React.useState({});
  const dataProvider = useDataProvider();
  const stripe = useStripe();
  const elements = useElements();
  const notify = useNotify();
  const redirect = useRedirect();

  const handleChange = (e) => {
    setCard({ ...card, cardHolder: e.target.value });
  };

  const aquireClientSecret = async () => {
    const result = await dataProvider.getOne(
      "billing/credit-card/change-card-intent",
      { id: "" }
    );

    if (!result.data) {
      notify(`Currently unable to add a new cards`, "warning");
      return null;
    }
    return result.data.clientSecret;
  };

  const updateCreditCard = async (clientSecret) => {
    if (!stripe || !elements) {
      return;
    }

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: card.cardHolder,
        },
      },
    });

    if (result.error) {
      notify(`Card confirmation failed: ${result.error.message}`, "warning");
      return null;
    }
    return result.setupIntent.payment_method;
  };

  const handleSubmit = async () => {
    setLoading(true);
    const clientSecret = await aquireClientSecret();
    const defaultPaymentMethod = await updateCreditCard(clientSecret);

    if (!defaultPaymentMethod) {
      setLoading(false);
      return;
    }

    return dataProvider
      .update("billing/credit-card", {
        id: "",
        data: { paymentMethodId: defaultPaymentMethod },
      })
      .then(({ data }) => {
        notify(`Your update was successful.`, "success");
        redirect("/users/profile");
      })
      .catch((error) => {
        notify(`Could update payment information: ${error.message}`, "warning");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={12}>
        <TextField
          label="Card Holder"
          name="cardHolder"
          variant="outlined"
          required
          fullWidth={true}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12} sm={12}>
        <TextField
          label="Credit Card Number"
          name="ccnumber"
          variant="outlined"
          required
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: StripeInput,
            inputProps: {
              component: CardNumberElement,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Expiration Date"
          name="ccexp"
          variant="outlined"
          required
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: StripeInput,
            inputProps: {
              component: CardExpiryElement,
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="CVC"
          name="cvc"
          variant="outlined"
          required
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: StripeInput,
            inputProps: {
              component: CardCvcElement,
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        {loading ? (
          <SaveButton disabled={true} icon={<CircularProgress size={16} />} />
        ) : (
          <SaveButton handleSubmitWithRedirect={handleSubmit} />
        )}
      </Grid>
    </Grid>
  );
};

export const CardEdit = ({ history: { goBack }, ...props }) => {
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);
  return (
    <Card>
      <CardHeader title="Edit Credit Card" />
      <Button startIcon={<ArrowLeft />} onClick={goBack}>
        Back
      </Button>
      <CardContent>
        <Elements stripe={stripePromise}>
          <CardEditContent />
        </Elements>
      </CardContent>
    </Card>
  );
};
